import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, Mail, Gift, Users, CheckCircle2, Clock } from "lucide-react";

const WHATSAPP_MSG = (code, url) =>
  `Hey! I've been training with Coach JP — former French national team runner. He builds personalised programs that actually work. Use my code ${code} when you sign up and we both get a FREE week of coaching. ${url}`;

const EMAIL_SUBJECT = "Join me on Coach JP — free week of premium running coaching";
const EMAIL_BODY = (code, url) =>
  `Hey,\n\nI've been training with Coach JP and the programs are seriously next-level — built around your goals, your pace, your recovery.\n\nUse my referral code ${code} when you sign up and we'll both get a FREE week of coaching.\n\nSign up here: ${url}\n\nLet's run.`;

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load referrals"))
      .finally(() => setLoading(false));
  }, []);

  const copyToClipboard = (text, label = "Copied") => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(label));
    } else {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(label); } catch { toast.error("Copy failed"); }
      document.body.removeChild(ta);
    }
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(WHATSAPP_MSG(data.code, data.share_url));
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const shareEmail = () => {
    const subject = encodeURIComponent(EMAIL_SUBJECT);
    const body = encodeURIComponent(EMAIL_BODY(data.code, data.share_url));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Coach JP — Premium Running Coaching",
          text: WHATSAPP_MSG(data.code, data.share_url),
          url: data.share_url,
        });
      } catch { /* user cancelled */ }
    } else {
      copyToClipboard(data.share_url, "Link copied — share anywhere");
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="text-[#888] font-mono-jp text-xs uppercase tracking-widest" data-testid="referrals-loading">Loading…</div>
      </AppShell>
    );
  }

  if (!data) return <AppShell><div className="text-[#888]">No data</div></AppShell>;

  return (
    <AppShell>
      <div data-testid="referrals-page">
        {/* Header */}
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-2">
          <Gift className="w-3.5 h-3.5" /> REFER & EARN
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl uppercase leading-none">
          Give a week.<br /><span className="text-[#d7ff00]">Get a week.</span>
        </h1>
        <p className="mt-4 text-[#bbb] text-sm max-w-xl">
          Share your code. When a friend signs up and pays for their first program, you both get <span className="text-[#d7ff00]">+1 free week</span> of premium coaching added to your training.
        </p>

        {/* Code hero */}
        <div className="mt-8 bg-[#0a0a0a] border border-[#d7ff00]/30 p-6 sm:p-8">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">YOUR REFERRAL CODE</div>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="font-display text-3xl sm:text-5xl tracking-wider text-[#d7ff00] break-all" data-testid="referral-code">{data.code}</div>
            <button
              onClick={() => copyToClipboard(data.code, "Code copied")}
              className="flex items-center gap-2 border border-[#d7ff00] text-[#d7ff00] hover:bg-[#d7ff00] hover:text-black px-4 py-2 font-mono-jp text-[10px] uppercase tracking-widest transition"
              data-testid="copy-code-btn">
              <Copy className="w-3.5 h-3.5" /> COPY CODE
            </button>
          </div>

          {/* Share URL */}
          <div className="mt-6">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">SHARE LINK</div>
            <div className="mt-2 flex items-center gap-2 bg-[#070707] border border-[#222] px-3 py-2.5">
              <span className="font-mono-jp text-xs text-[#bbb] truncate flex-1" data-testid="referral-url">{data.share_url}</span>
              <button onClick={() => copyToClipboard(data.share_url, "Link copied")} className="text-[#d7ff00] hover:text-white" data-testid="copy-url-btn" aria-label="Copy link">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Share buttons */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button onClick={shareWhatsApp} className="flex items-center justify-center gap-2 bg-[#25D366] text-black font-display tracking-wider uppercase text-sm py-3 hover:opacity-90" data-testid="share-whatsapp">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              WHATSAPP
            </button>
            <button onClick={shareEmail} className="flex items-center justify-center gap-2 bg-[#d7ff00] text-black font-display tracking-wider uppercase text-sm py-3 hover:bg-white" data-testid="share-email">
              <Mail className="w-4 h-4" /> EMAIL
            </button>
            <button onClick={shareNative} className="flex items-center justify-center gap-2 border border-[#222] text-white font-display tracking-wider uppercase text-sm py-3 hover:border-[#d7ff00] hover:text-[#d7ff00]" data-testid="share-more">
              <Share2 className="w-4 h-4" /> MORE
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1f1f1f]">
          <Stat label="INVITED" value={data.stats.invited} icon={Users} testid="stat-invited" />
          <Stat label="SIGNED UP" value={data.stats.pending + data.stats.converted} icon={Users} testid="stat-signed" />
          <Stat label="CONVERTED" value={data.stats.converted} icon={CheckCircle2} accent testid="stat-converted" />
          <Stat label="WEEKS EARNED" value={data.stats.weeks_earned} icon={Gift} accent testid="stat-weeks" />
        </div>

        {/* Referrals list */}
        <div className="mt-8">
          <h2 className="font-display text-2xl uppercase tracking-wider">Your invites</h2>
          {data.referrals.length === 0 ? (
            <div className="mt-4 bg-[#0a0a0a] border border-[#1f1f1f] p-6 text-center" data-testid="referrals-empty">
              <Users className="w-8 h-8 text-[#444] mx-auto" />
              <p className="mt-3 text-[#888] text-sm">No invites yet. Share your code to start earning free weeks.</p>
            </div>
          ) : (
            <div className="mt-4 divide-y divide-[#1f1f1f] border border-[#1f1f1f]" data-testid="referrals-list">
              {data.referrals.map((r, i) => (
                <div key={r.id || `${r.referee_name}-${r.created_at}-${i}`} className="flex items-center justify-between px-4 py-3 bg-[#0a0a0a]">
                  <div>
                    <div className="font-display text-base uppercase tracking-wider">{r.referee_name}</div>
                    <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#666]">
                      {new Date(r.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </div>
                  {r.status === "converted" ? (
                    <span className="flex items-center gap-1.5 text-[#d7ff00] font-mono-jp text-[10px] uppercase tracking-widest">
                      <CheckCircle2 className="w-3.5 h-3.5" /> +1 WEEK
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[#888] font-mono-jp text-[10px] uppercase tracking-widest">
                      <Clock className="w-3.5 h-3.5" /> PENDING
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="mt-10 bg-[#0a0a0a] border border-[#1f1f1f] p-6">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">HOW IT WORKS</div>
          <ol className="mt-3 space-y-2 text-sm text-[#bbb]">
            <li><span className="text-[#d7ff00] font-display">01.</span> Share your code or link with a runner friend.</li>
            <li><span className="text-[#d7ff00] font-display">02.</span> They sign up using your code and complete their first paid program.</li>
            <li><span className="text-[#d7ff00] font-display">03.</span> You both get <span className="text-[#d7ff00]">+1 free week</span> of coaching credited to your account.</li>
          </ol>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="bg-[#0a0a0a] p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{label}</div>
        <Icon className={`w-3.5 h-3.5 ${accent ? "text-[#d7ff00]" : "text-[#444]"}`} />
      </div>
      <div className={`mt-2 font-display text-3xl ${accent ? "text-[#d7ff00]" : "text-white"}`}>{value}</div>
    </div>
  );
}
